import {
  all,
  call,
  delay,
  fork,
  put,
  select,
  take,
  takeEvery
} from 'redux-saga/effects';
import request from '@iso/lib/helpers/httpClient';
import { deserializeKeys, serializeKeys } from '@iso/lib/helpers/keyNormalizer';
import * as ACTIONS from './actions';
import * as CONSTANTS from './constants';
import queryString from 'query-string';
import pickBy from 'lodash/pickBy';
import isUndefined from 'lodash/isUndefined';
import notify from '@iso/lib/helpers/notify';
import { showServerError } from '@iso/lib/helpers/utility';

const ITEMS_PER_PAGE = 20;

export function* fetchAllThreads({ isStartFromScratch = false }) {
  try {
    const page = yield select((state) => state.BuddiBot.pagination.page);
    const paramPage = isStartFromScratch ? 1 : page;
    const params = queryString.stringify(
      pickBy(
        {
          offset: (paramPage - 1) * ITEMS_PER_PAGE,
          limit: ITEMS_PER_PAGE
        },
        (item) => !isUndefined(item)
      )
    );
    const data = yield call(
      request,
      `/buddi-bot/chats?${params}`,
      'GET',
      null,
      true
    );

    if (isStartFromScratch) {
      const el = document.getElementById('buddiBotThreads');
      if (el) {
        const child = el.querySelector('.infinite-scroll-component');
        child.scrollTo({
          top: 0,
          smooth: true
        });
      }
    }

    yield put(
      ACTIONS.fetchAllThreadsSuccess({
        isStartFromScratch,
        data: serializeKeys(data)
      })
    );
  } catch (error) {
    notify('error', showServerError(error));
    yield put(ACTIONS.fetchAllThreadsFail(error));
  }
}

function* generateText(prompt) {
  const words = prompt.split(' ');

  for (let j = 0; j < words.length; j++) {
    yield put(ACTIONS.setText(words.slice(0, j + 1).join(' ')));
    yield delay(50);
  }

  yield put({ type: CONSTANTS.STOP_TEXT_GENERATION });
}

function* watchTextGeneration() {
  while (true) {
    const { payload } = yield take(CONSTANTS.START_TEXT_GENERATION);
    yield call(generateText, payload);
  }
}

function* watchStopTextGeneration() {
  yield take(CONSTANTS.STOP_TEXT_GENERATION);
}

export function* createThread({ payload }) {
  try {
    const body = {
      newMessageText: payload
    };

    const data = yield call(
      request,
      `/buddi-bot/chats`,
      'POST',
      deserializeKeys(body),
      true
    );

    yield put(ACTIONS.createThreadSuccess(serializeKeys(data)));
    yield fetchAllThreads({ isStartFromScratch: true });
    yield put(ACTIONS.startTextGeneration(data.messages[1].text));
  } catch (error) {
    notify('error', showServerError(error));
    yield put(ACTIONS.createThreadSFail(error));
  }
}

export function* sendPrompt({ payload }) {
  try {
    const id = yield select((state) => state.BuddiBot.selectedChatId);
    const body = {
      newMessageText: payload
    };

    const data = yield call(
      request,
      `/buddi-bot/chats/${id}`,
      'POST',
      deserializeKeys(body),
      true
    );
    const serializedData = serializeKeys(data);
    yield put(ACTIONS.sendNewPromptSuccess());
    yield put(ACTIONS.startTextGeneration(serializedData.message));
  } catch (error) {
    notify('error', showServerError(error));
    yield put(ACTIONS.sendNewPromptFail(error));
  }
}

export function* fetchThread({ payload }) {
  try {
    const data = yield call(
      request,
      `/buddi-bot/chats/${payload.id}`,
      'GET',
      null,
      true
    );
    yield put(ACTIONS.fetchThreadSuccess(serializeKeys(data)));
  } catch (error) {
    notify('error', showServerError(error));
    yield put(ACTIONS.fetchThreadFail(error));
  }
}

export function* renameThread({ payload }) {
  try {
    const body = {
      label: payload.label
    };

    const data = yield call(
      request,
      `/buddi-bot/chats/${payload.id}`,
      'PATCH',
      deserializeKeys(body),
      true
    );
    yield put(ACTIONS.renameThreadSuccess(serializeKeys(data)));
  } catch (error) {
    notify('error', showServerError(error));
    yield put(ACTIONS.renameThreadFail(error));
  }
}

export function* deleteThread({ id }) {
  try {
    yield call(request, `/buddi-bot/chats/${id}`, 'DELETE', null, true);
    yield put(ACTIONS.deleteThreadSuccess({ id }));
    yield fetchAllThreads({ isStartFromScratch: true });
  } catch (error) {
    notify('error', showServerError(error));
    yield put(ACTIONS.deleteThreadFail(error));
  }
}

export default function* buddiBotSaga() {
  yield takeEvery(CONSTANTS.CREATE_THREAD_REQUEST, createThread);
  yield takeEvery(CONSTANTS.SEND_NEW_PROMPT_REQUEST, sendPrompt);
  yield takeEvery(CONSTANTS.FETCH_THREADS_REQUEST, fetchAllThreads);
  yield takeEvery(CONSTANTS.FETCH_THREAD_REQUEST, fetchThread);
  yield takeEvery(CONSTANTS.RENAME_THREAD_REQUEST, renameThread);
  yield takeEvery(CONSTANTS.DELETE_THREAD_REQUEST, deleteThread);
  yield all([fork(watchTextGeneration), fork(watchStopTextGeneration)]);
}
